import React from 'react';
import { QuestionField } from './QuestionField';
import { GroupField } from './GroupField';
import { MapWithMarker } from './CustomFields/MapWithMarker';
import {
  groupFieldsIntoRows,
  groupFieldsForHotelLocation,
  filterExcludedQuestions,
} from './utils';
import {
  FormSection,
  SectionHeader,
  SectionTitle,
  SectionDescription,
  QuestionRow,
  RowNote,
  RowNoteText,
  HotelLocationContainer,
  LocationFieldsContainer,
  LocationFieldWrapper,
  MapContainer,
} from './DynamicQuestionForm.styled';
import { DynamicQuestionFormProps } from '../../../types/questionForm';

export const DynamicQuestionForm: React.FC<DynamicQuestionFormProps> = ({
  isGrouped = false,
  metadata,
  answers,
  onAnswerChange,
  hotel,
  hasCountryChangedRef: parentHasCountryChangedRef,
  hasDestinationChangedRef: parentHasDestinationChangedRef,
}) => {
  // Create local refs if not provided by parent
  const localCountryChangedRef = React.useRef(false);
  const localDestinationChangedRef = React.useRef(false);

  // Use parent refs if provided, otherwise use local refs
  const hasCountryChangedRef =
    parentHasCountryChangedRef || localCountryChangedRef;
  const hasDestinationChangedRef =
    parentHasDestinationChangedRef || localDestinationChangedRef;

  // Filter out excluded questions
  const visibleFields = filterExcludedQuestions(metadata.fields);

  // Check if this is a hotel location form (has map component)
  const hasMapComponent = visibleFields.some(
    field => field.type === 'custom' && field.component === 'map-with-marker'
  );

  const isHotelLocationForm =
    hasMapComponent &&
    (metadata.title.toLowerCase().includes('location') ||
      metadata.title.toLowerCase().includes('hotel'));

  const renderField = (question: any) => {
    if (question.type === 'group') {
      return (
        <GroupField
          question={question}
          answers={answers}
          onAnswerChange={onAnswerChange}
        />
      );
    }

    return (
      <QuestionField
        question={question}
        value={answers[question.id]?.value ?? question.defaultValue}
        onChange={(value: any) => onAnswerChange(question.id, value)}
        answers={answers}
        onAnswerChange={onAnswerChange}
        // Note: hasCountryChangedRef and hasDestinationChangedRef are NOT passed here
        // They are only passed for hotel location fields in renderHotelLocationLayout
      />
    );
  };

  const renderHotelLocationLayout = () => {
    const { locationFields, mapField, remainingFields } =
      groupFieldsForHotelLocation(visibleFields);

    return (
      <>
        {/* Location fields with map layout */}
        <HotelLocationContainer>
          {/* Left side - Location fields */}
          <LocationFieldsContainer>
            {locationFields.map(question => (
              <LocationFieldWrapper key={question.id}>
                <QuestionField
                  question={question}
                  value={answers[question.id]?.value ?? question.defaultValue}
                  onChange={(value: any) => onAnswerChange(question.id, value)}
                  answers={answers}
                  onAnswerChange={onAnswerChange}
                  hasCountryChangedRef={hasCountryChangedRef}
                  hasDestinationChangedRef={hasDestinationChangedRef}
                />
              </LocationFieldWrapper>
            ))}
          </LocationFieldsContainer>

          {/* Right side - Map */}
          {mapField && (
            <MapContainer>
              <MapWithMarker
                question={mapField}
                value={answers[mapField.id]?.value ?? mapField.defaultValue}
                onChange={(value: any) => onAnswerChange(mapField.id, value)}
                latitude={hotel?.hotel_location_latitude}
                longitude={hotel?.hotel_location_longitude}
                hotel={{
                  city: hotel?.hotel_location_city,
                  country: hotel?.hotel_location_country,
                  ...hotel,
                }}
              />
            </MapContainer>
          )}
        </HotelLocationContainer>

        {/* Remaining fields in normal layout */}
        {remainingFields.length > 0 && (
          <>
            {groupFieldsIntoRows(remainingFields).map((row, rowIndex) => {
              const isSingleColumn = row.length === 1 && row[0].colSpan === 2;

              return (
                <QuestionRow
                  key={`remaining-${rowIndex}`}
                  className={isSingleColumn ? 'single-column' : ''}
                >
                  {row.map(question => (
                    <div
                      key={question.id}
                      style={{
                        gridColumn:
                          question.colSpan === 2 ? 'span 2' : 'span 1',
                      }}
                    >
                      {renderField(question)}
                    </div>
                  ))}
                </QuestionRow>
              );
            })}
          </>
        )}
      </>
    );
  };

  const renderNormalLayout = () => {
    const fieldRows = groupFieldsIntoRows(visibleFields);

    return fieldRows.map((row, rowIndex) => {
      const isSingleColumn = row.length === 1 && row[0].colSpan === 2;

      return (
        <QuestionRow
          key={rowIndex}
          className={isSingleColumn ? 'single-column' : ''}
        >
          {row.map(question => (
            <div
              key={question.id}
              style={{
                gridColumn: question.colSpan === 2 ? 'span 2' : 'span 1',
              }}
            >
              {renderField(question)}
            </div>
          ))}
        </QuestionRow>
      );
    });
  };

  return (
    <FormSection>
      <SectionHeader>
        {!isGrouped && (
          <SectionTitle $isGrouped={isGrouped}>
            {metadata.title.replaceAll('+', '&')}
          </SectionTitle>
        )}
        {metadata.description && (
          <SectionDescription>{metadata.description}</SectionDescription>
        )}
      </SectionHeader>

      {isHotelLocationForm ? renderHotelLocationLayout() : renderNormalLayout()}

      {metadata.notes && (
        <RowNote>
          <RowNoteText>{metadata.notes}</RowNoteText>
        </RowNote>
      )}
    </FormSection>
  );
};
